import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarClock, Home, RefreshCw, Sparkles, TableProperties,
} from 'lucide-react';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import { cn } from '../utils/cn';
import ClassicScheduleView from '../components/schedule/ClassicScheduleView';
import ProScheduleView from '../components/schedule/pro/ProScheduleView';
import PositionsScheduleView from '../components/schedule/PositionsScheduleView';
import ReconciliationStatusPill from '../components/reconciliation/ReconciliationStatusPill';
import { useScheduleData } from '../components/schedule/useScheduleData';
import type { ScheduleView } from '../components/schedule/useScheduleData';
import type { ScheduleViewMode } from '../types/schedule';
import { LabeledControl, ModeRadio } from '../components/schedule/ScopeModeRadio';
const VIEW_KEY = 'schedule.view';
const MODE_KEY = 'schedule.mode';
// Phase A migration: read the legacy `schedule.viewMode` key once so users
// don't lose their persisted Classic/Pro preference on first load.
const LEGACY_VIEW_KEY = 'schedule.viewMode';

// User-facing Mode is now a binary scope toggle: Broker (today's date-scoped)
// vs Platform (full cross-date). The Entries scope was an admin-only
// diagnostic; it's been removed from the UI for everyone, but the backend
// still accepts the value — so any persisted / shared `?mode=entries` URL
// or localStorage entry is silently coerced back to 'default' (Broker)
// here rather than 400ing in the API.
function readSavedView(): ScheduleView {
  try {
    const v = localStorage.getItem(VIEW_KEY) ?? localStorage.getItem(LEGACY_VIEW_KEY);
    return v === 'pro' || v === 'positions' ? v : 'classic';
  } catch {
    return 'classic';
  }
}

function readSavedMode(fallback: ScheduleViewMode = 'default'): ScheduleViewMode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    if (v === 'default' || v === 'full') return v;
    return fallback;
  } catch {
    return fallback;
  }
}

function isValidView(v: string | null): v is ScheduleView {
  return v === 'classic' || v === 'pro' || v === 'positions';
}

// Note: 'entries' is intentionally NOT a valid Mode here even though the
// backend still understands it — the radio surface is binary now.
function isValidMode(v: string | null): v is 'default' | 'full' {
  return v === 'default' || v === 'full';
}

/**
 * Schedule page shell — owns view + mode state and renders the active view.
 *
 * URL is the canonical source of truth for `?view=` and `?mode=`. localStorage
 * is the fallback hydration when the URL is silent. Date is intentionally NOT
 * persisted to localStorage — always today on fresh load (operator expectation).
 *
 * Page header carries two grouped controls: Scope (Broker · Platform) and
 * View (Classic · Pro · Positions). Each group has its own micro-label so
 * the two controls read as distinct concerns, separated by a thin divider.
 */
const SchedulePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlView = searchParams.get('view');
  const urlMode = searchParams.get('mode');

  // Mount-only snapshots so the hook's lazy initializers don't re-evaluate
  // on every render.
  const initialViewRef = React.useRef<ScheduleView | null>(null);
  if (initialViewRef.current === null) {
    initialViewRef.current = isValidView(urlView) ? urlView : readSavedView();
  }
  const initialModeRef = React.useRef<ScheduleViewMode | null>(null);
  if (initialModeRef.current === null) {
    initialModeRef.current = isValidMode(urlMode) ? urlMode : readSavedMode('default');
  }

  const data = useScheduleData({
    initialView: () => initialViewRef.current as ScheduleView,
    initialViewMode: () => initialModeRef.current as ScheduleViewMode,
  });
  const { view, setView, setViewMode, fetchSummary, refreshing } = data;

  // Scope mirrored as local state because the URL needs to reflect it and
  // the hook's viewMode is canonical. Push effect below keeps them aligned.
  const [mode, setModeState] = React.useState<ScheduleViewMode>(
    () => initialModeRef.current as ScheduleViewMode,
  );

  // Persist on change + sync to URL.
  React.useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, view); } catch { /* noop */ }
  }, [view]);
  React.useEffect(() => {
    try { localStorage.setItem(MODE_KEY, mode); } catch { /* noop */ }
  }, [mode]);

  // Single effect keeps URL in lock-step with state (defaults stay implicit —
  // we only write the param when it differs from the default to keep URLs
  // tidy for sharing).
  React.useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (view === 'classic') next.delete('view'); else next.set('view', view);
    if (mode === 'default') next.delete('mode'); else next.set('mode', mode);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: false });
    }
  }, [view, mode, searchParams, setSearchParams]);

  // Hydrate from URL if back/forward navigation changes search params.
  // 'entries' from a legacy URL is silently dropped — falls back to the
  // current mode (effectively a no-op).
  React.useEffect(() => {
    if (isValidView(urlView) && urlView !== view) setView(urlView);
    if (isValidMode(urlMode) && urlMode !== mode) setModeState(urlMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlView, urlMode]);

  // Push page-level Mode into the data hook.
  React.useEffect(() => {
    setViewMode(mode);
  }, [mode, setViewMode]);

  const isBrokerScope = mode === 'default';

  return (
    <StandardLayout>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2 flex-wrap">
            Execution Schedule
            <span
              className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
              aria-label={`Active scope: ${isBrokerScope ? 'Broker' : 'Platform'} view`}
              title={isBrokerScope
                ? "Broker view — date-scoped, matches broker's positions screen"
                : 'Platform view — full cross-date scope, includes carry from prior dates'}
            >
              {isBrokerScope ? 'Broker view' : 'Platform view'}
            </span>
          </span>
        }
        pageType="management"
        icon={<CalendarClock className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Execution Schedule', icon: <CalendarClock className="w-4 h-4" /> },
        ]}
        actions={
          <div className="flex items-center gap-4 flex-wrap">
            {/* Selectors cluster — Scope + View are "what to show"
                controls. Inner thin divider distinguishes the two
                labeled toggle groups from each other. */}
            <div className="flex items-center gap-3 flex-wrap">
              <LabeledControl label="Scope">
                <ModeRadio mode={mode} onChange={setModeState} />
              </LabeledControl>
              <span
                aria-hidden="true"
                className="hidden sm:block h-5 w-px bg-gray-300 dark:bg-gray-600"
              />
              <LabeledControl label="View">
                <ViewTabs view={view} onChange={setView} />
              </LabeledControl>
            </div>

            {/* Stronger separator marks the seam between selectors and
                data controls so users don't read them as one mega-toggle. */}
            <span
              aria-hidden="true"
              className="hidden sm:block h-6 w-px bg-gray-400 dark:bg-gray-500"
            />

            {/* Data-controls cluster — status indicator + freshness
                action. Reconciliation pill exposes broker-vs-platform
                consistency state; Refresh re-pulls the active fetch
                (summary or positions, per view). */}
            <div className="flex items-center gap-2">
              <ReconciliationStatusPill />
              <button
                type="button"
                onClick={fetchSummary}
                disabled={refreshing}
                title="Refresh data"
                className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        }
      />

      {/* Each view renders the SharedToolbar between its hero banner and
          table — closer to the data, lets each view supply its own count
          and exitable predicate. */}
      {view === 'positions' ? (
        <PositionsScheduleView data={data} />
      ) : view === 'pro' ? (
        <ProScheduleView data={data} />
      ) : (
        <ClassicScheduleView data={data} />
      )}
    </StandardLayout>
  );
};

/** View tabs — Classic / Pro / Positions. All users can access all views. */
const ViewTabs: React.FC<{
  view: ScheduleView;
  onChange: (v: ScheduleView) => void;
}> = ({ view, onChange }) => (
  <div
    className="inline-flex items-center bg-gray-100 dark:bg-gray-700/60 rounded-md p-0.5"
    role="tablist"
    aria-label="Schedule view"
  >
    <button
      type="button"
      role="tab"
      aria-selected={view === 'classic'}
      onClick={() => onChange('classic')}
      className={cn(
        'px-3 py-1.5 text-xs font-medium rounded transition-colors',
        view === 'classic'
          ? 'bg-blue-600 text-white shadow-sm'
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
      )}
    >
      Classic
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={view === 'pro'}
      onClick={() => onChange('pro')}
      className={cn(
        'px-3 py-1.5 text-xs font-medium rounded transition-colors inline-flex items-center gap-1',
        view === 'pro'
          ? 'bg-blue-600 text-white shadow-sm'
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
      )}
    >
      <Sparkles className="w-3 h-3" />
      Pro
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={view === 'positions'}
      onClick={() => onChange('positions')}
      className={cn(
        'px-3 py-1.5 text-xs font-medium rounded transition-colors inline-flex items-center gap-1',
        view === 'positions'
          ? 'bg-blue-600 text-white shadow-sm'
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
      )}
    >
      <TableProperties className="w-3 h-3" />
      Positions
    </button>
  </div>
);

export default SchedulePage;
